import React, { useCallback } from 'react';
import Icon from '../../../../components/common/Icon/Icon';
import styles from './NumericStepperInput.module.css';

interface NumericStepperInputProps {
  id?: string;
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  hasError?: boolean;
  size?: 'field' | 'detail';
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const parseCurrentValue = (value: string | number | null | undefined) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const parsedValue = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const stepIntegerValue = (
  value: string | number | null | undefined,
  delta: number,
  min: number,
  max: number,
) => {
  const currentValue = parseCurrentValue(value) ?? 0;
  return String(Math.min(max, Math.max(min, currentValue + delta)));
};

const NumericStepperInput: React.FC<NumericStepperInputProps> = ({
  id,
  value,
  onChange,
  min = 0,
  max = 999,
  disabled = false,
  readOnly = false,
  placeholder,
  ariaLabel,
  hasError = false,
  size = 'field',
  onKeyDown,
}) => {
  const displayValue = value === null || value === undefined ? '' : String(value);
  const currentValue = parseCurrentValue(value);
  const isLocked = disabled || readOnly;
  const canDecrement = !isLocked && (currentValue === null || currentValue > min);
  const canIncrement = !isLocked && (currentValue === null || currentValue < max);

  const handleStep = useCallback((delta: number) => {
    if (isLocked) {
      return;
    }

    onChange(stepIntegerValue(value, delta, min, max));
  }, [isLocked, max, min, onChange, value]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      handleStep(1);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      handleStep(-1);
      return;
    }

    onKeyDown?.(event);
  }, [handleStep, onKeyDown]);

  return (
    <div
      className={`${styles.wrapper} ${styles[size]}${hasError ? ` ${styles.error}` : ''}${isLocked ? ` ${styles.disabled}` : ''}`}
    >
      <input
        id={id}
        className={styles.input}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        disabled={disabled}
        readOnly={readOnly}
      />
      <div className={styles.stepper}>
        <button
          type="button"
          className={styles.stepperButton}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => handleStep(1)}
          disabled={!canIncrement}
          aria-label="Aumentar valor"
          tabIndex={-1}
        >
          <Icon name="keyboard_arrow_up" size={14} />
        </button>
        <button
          type="button"
          className={styles.stepperButton}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => handleStep(-1)}
          disabled={!canDecrement}
          aria-label="Diminuir valor"
          tabIndex={-1}
        >
          <Icon name="keyboard_arrow_down" size={14} />
        </button>
      </div>
    </div>
  );
};

export default NumericStepperInput;
